// Team: who signs in to the brand's workspace, and as what. Everyone sees the
// list; an Admin invites a colleague by email, changes a role, sends an
// invitation again or removes someone (shared/brand-team.ts). Each person
// signs in with their own address, so the activity log names who did what.

import {useCallback, useEffect, useRef, useState} from 'react';
import type {FormEvent, ReactNode} from 'react';
import {BRAND_INVITE_DAYS, BRAND_ROLE_LABEL, BRAND_ROLE_SUMMARY, BRAND_ROLES} from '../../shared/brand-team';
import type {BrandRole, BrandTeamMember, BrandTeamResponse} from '../../shared/brand-team';
import {Icon} from '../creator/components/Icon';
import type {IconName} from '../creator/components/Icon';
import {BrandApiError, changeTeam, describe, fetchTeam} from './api';
import {initials} from './format';
import {SettingsHead} from './settings';
import {usePageTitle} from './page-title';
import {useBrand} from './store';
import './team.css';

function ago(value: string | null): string {
  if (!value) return '';
  const days = Math.floor((Date.now() - Date.parse(value)) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(value).toLocaleDateString(undefined, {day: 'numeric', month: 'short', year: 'numeric'});
}

/** The quiet line under a person's name: when they were last here, or who invited them. */
function standing(member: BrandTeamMember): string {
  if (member.status === 'expired') return `Sent ${ago(member.invited_at)}, not accepted`;
  if (member.status === 'invited') return `Sent ${ago(member.invited_at)}${member.invited_by ? ` by ${member.invited_by}` : ''}`;
  if (member.last_seen_at) return `Active ${ago(member.last_seen_at)}`;
  return member.joined_at ? `Joined ${ago(member.joined_at)}` : '';
}

/** What a change did, with the API keys it revoked. */
function withKeys(done: string, team: BrandTeamResponse): string {
  const keys = team.keys_revoked ?? 0;
  return keys ? `${done}. ${keys === 1 ? 'Their API key was' : `Their ${keys} API keys were`} revoked.` : done;
}

/** The one place a role is chosen: two cards, each saying what the role can do. */
function RolePicker({value, onChange, disabled}: {value: BrandRole; onChange(role: BrandRole): void; disabled?: boolean}) {
  return (
    <div className="role-picker" role="radiogroup" aria-label="Role">
      {BRAND_ROLES.map((role) => (
        <label key={role} className={`role-option${value === role ? ' chosen' : ''}`}>
          <input type="radio" name="invite-role" value={role} checked={value === role} disabled={disabled} onChange={() => onChange(role)} />
          <span className="role-mark" aria-hidden="true"><Icon name="check" size={13} /></span>
          <span className="role-text"><b>{BRAND_ROLE_LABEL[role]}</b><small>{BRAND_ROLE_SUMMARY[role]}</small></span>
        </label>
      ))}
    </div>
  );
}

interface MenuAction {
  key: string;
  label: string;
  icon: IconName;
  danger?: boolean;
  /** Asked first, inside the menu, for what can't be taken back. */
  confirm?: {title: string; detail: string; button: string};
  run(): void;
}

/** A row's actions behind one button, so the list stays calm; what can't be undone asks first. */
function RowMenu({label, actions, disabled}: {label: string; actions: MenuAction[]; disabled: boolean}) {
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState<MenuAction | null>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = () => { setOpen(false); setAsking(null); };
    const outside = (event: MouseEvent) => { if (!box.current?.contains(event.target as Node)) close(); };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') close(); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', escape); };
  }, [open]);
  function choose(action: MenuAction) {
    if (action.confirm && asking !== action) { setAsking(action); return; }
    setOpen(false); setAsking(null);
    action.run();
  }
  return (
    <div className="row-menu" ref={box}>
      <button className="row-menu-trigger" type="button" aria-label={`Actions for ${label}`} aria-haspopup="menu" aria-expanded={open}
        disabled={disabled} onClick={() => { setOpen((value) => !value); setAsking(null); }}>
        <Icon name="more" size={18} />
      </button>
      {open ? (
        <div className="row-menu-panel" role="menu">
          {asking?.confirm ? (
            <div className="row-menu-confirm">
              <b>{asking.confirm.title}</b>
              <p>{asking.confirm.detail}</p>
              <div>
                <button className="btn btn-light btn-sm" type="button" onClick={() => setAsking(null)}>Keep</button>
                <button className="btn btn-danger btn-sm" type="button" autoFocus onClick={() => choose(asking)}>{asking.confirm.button}</button>
              </div>
            </div>
          ) : actions.map((action) => (
            <button key={action.key} type="button" role="menuitem" className={action.danger ? 'danger' : ''} onClick={() => choose(action)}>
              <Icon name={action.icon} size={16} />{action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function MemberRow({member, me, canManage, onChanged, onStale}: {
  member: BrandTeamMember; me: string; canManage: boolean; onChanged(team: BrandTeamResponse): void; onStale(): void;
}) {
  const {toast, signOut} = useBrand();
  const [busy, setBusy] = useState(false);
  const self = member.email.toLowerCase() === me.toLowerCase();
  const editable = canManage && !member.owner && !self;
  const label = member.name || member.email;
  const pending = member.status !== 'active';

  async function act(run: () => Promise<BrandTeamResponse & {sent?: boolean}>, done: string) {
    setBusy(true);
    try {
      const team = await run();
      onChanged(team);
      toast(team.sent === false ? 'Saved, but the email could not be sent. Try again in a minute.' : withKeys(done, team));
    } catch (error) {
      toast(describe(error), true);
      // Another Admin got there first: show the team as it is now.
      if (error instanceof BrandApiError && error.code === 'member_not_found') onStale();
    } finally {
      setBusy(false);
    }
  }

  async function leave() {
    setBusy(true);
    try {
      await changeTeam({action: 'leave'});
      await signOut();
    } catch (error) {
      toast(describe(error), true);
      setBusy(false);
    }
  }

  const actions: MenuAction[] = [];
  if (editable && pending) {
    actions.push({key: 'resend', label: 'Send invitation again', icon: 'send',
      run: () => void act(() => changeTeam({action: 'resend', id: member.id}), `Invitation sent again to ${member.email}`)});
    actions.push({key: 'cancel', label: 'Cancel invitation', icon: 'x', danger: true,
      confirm: {title: `Cancel ${label}’s invitation?`, detail: 'The link in their email stops working.', button: 'Cancel invitation'},
      run: () => void act(() => changeTeam({action: 'remove', id: member.id}), `Invitation to ${member.email} cancelled`)});
  } else if (editable) {
    actions.push({key: 'remove', label: 'Remove from team', icon: 'trash', danger: true,
      confirm: {title: `Remove ${label}?`, detail: 'They are signed out at once, and API keys they made stop working.', button: 'Remove'},
      run: () => void act(() => changeTeam({action: 'remove', id: member.id}), `${label} can no longer sign in`)});
  } else if (self && !member.owner) {
    actions.push({key: 'leave', label: 'Leave team', icon: 'logout', danger: true,
      confirm: {title: 'Leave this team?', detail: 'You are signed out, and only an Admin can invite you back.', button: 'Leave team'},
      run: () => void leave()});
  }

  return (
    <article className={`member-row${pending ? ' pending' : ''}${busy ? ' busy' : ''}`}>
      <span className="avatar">{initials(member.name, member.email)}</span>
      <div className="member-who">
        <b>
          <span className="member-name">{label}</span>
          {self ? <span className="member-tag">You</span> : null}
          {member.owner ? <span className="member-tag">Owner</span> : null}
          {member.status === 'invited' ? <span className="member-tag invited">Invited</span> : null}
          {member.status === 'expired' ? <span className="member-tag expired">Expired</span> : null}
        </b>
        <small>{[member.name ? member.email : '', standing(member)].filter(Boolean).join(' · ')}</small>
      </div>
      <div className="member-role-cell">
        {editable ? (
          <select className="member-role" aria-label={`Role of ${label}`} value={member.role} disabled={busy}
            onChange={(event) => {
              const role = event.target.value as BrandRole;
              void act(() => changeTeam({action: 'role', id: member.id, role}), `${label} is now ${BRAND_ROLE_LABEL[role]}`);
            }}>
            {BRAND_ROLES.map((role) => <option key={role} value={role}>{BRAND_ROLE_LABEL[role]}</option>)}
          </select>
        ) : <span className="member-role-text">{BRAND_ROLE_LABEL[member.role]}</span>}
      </div>
      <div className="member-menu-cell">{actions.length ? <RowMenu label={label} actions={actions} disabled={busy} /> : null}</div>
    </article>
  );
}

export function TeamPage() {
  const {data, toast} = useBrand();
  usePageTitle('Team');
  const [team, setTeam] = useState<BrandTeamResponse | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<BrandRole>('manager');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [devLink, setDevLink] = useState('');
  const emailInput = useRef<HTMLInputElement>(null);
  const load = useCallback(() => fetchTeam().then(setTeam).catch((caught) => toast(describe(caught), true)), [toast]);
  useEffect(() => { void load(); }, [load]);
  if (!data) return null;
  const isAdmin = data.me.role === 'admin';
  const full = team ? team.members.length >= team.limit : false;

  async function invite(event: FormEvent) {
    event.preventDefault();
    const address = email.trim();
    if (!address) { setError('Enter their work email.'); emailInput.current?.focus(); return; }
    setBusy(true); setError('');
    try {
      const result = await changeTeam({action: 'invite', email: address, name: name.trim(), role});
      setTeam(result); setEmail(''); setName(''); setDevLink(result.dev_link || '');
      toast(result.sent === false ? 'Invitation saved, but the email could not be sent. Send it again from the list in a minute.' : `Invitation sent to ${address}`);
      emailInput.current?.focus();
    } catch (caught) {
      if (caught instanceof BrandApiError && caught.fields.email) { setError(caught.fields.email); emailInput.current?.focus(); }
      else toast(describe(caught), true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <SettingsHead />

      {isAdmin ? (
        <section className="card brand-section invite-card">
          <div className="section-head"><div><h2>Invite a colleague</h2><p>They get an email with a link to join, open for {BRAND_INVITE_DAYS} days. Everyone signs in with their own email, so you see who did what.</p></div></div>
          <form className="invite-form" onSubmit={(event) => void invite(event)} noValidate>
            <div className="invite-fields">
              <label className={`brand-field${error ? ' field-error' : ''}`}><span>Work email</span>
                <input ref={emailInput} type="email" value={email} onChange={(event) => { setEmail(event.target.value); setError(''); }}
                  placeholder="colleague@company.com" maxLength={254} aria-invalid={Boolean(error) || undefined} autoComplete="off" />
                {error ? <em>{error}</em> : null}
              </label>
              <label className="brand-field"><span>Name <small>Optional</small></span>
                <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Alex Kim" maxLength={120} autoComplete="off" />
              </label>
            </div>
            <div className="brand-field"><span>Role</span><RolePicker value={role} onChange={setRole} disabled={busy} /></div>
            <div className="invite-foot">
              <span className="muted">{full ? 'Your team is full. Remove someone to invite another person.' : `You can change the role later.`}</span>
              <button className="btn btn-g" disabled={busy || full}>{busy ? 'Sending…' : 'Send invite'}<Icon name="send" size={16} /></button>
            </div>
            {devLink ? <p className="invite-dev">Development, no email sent: <a href={devLink}>{devLink}</a></p> : null}
          </form>
        </section>
      ) : (
        <section className="card brand-section">
          <div className="section-head"><div><h2>You are a {BRAND_ROLE_LABEL[data.me.role]}</h2><p>Only an Admin invites people or changes roles.</p></div></div>
          <dl className="role-list">
            {BRAND_ROLES.map((item) => <div key={item} className={item === data.me.role ? 'mine' : ''}><dt>{BRAND_ROLE_LABEL[item]}</dt><dd>{BRAND_ROLE_SUMMARY[item]}</dd></div>)}
          </dl>
        </section>
      )}

      <section className="card brand-section">
        <div className="section-head"><div><h2>People</h2><p>{team ? `${team.members.length} of ${team.limit} places used` : 'Everyone who can sign in to this workspace'}</p></div></div>
        {team === null ? <div className="empty-state"><span className="loader" /></div> : (
          <div className="member-list">{team.members.map((member) => (
            <MemberRow key={member.id || 'owner'} member={member} me={data.me.email} canManage={isAdmin} onChanged={setTeam} onStale={() => void load()} />
          ))}</div>
        )}
      </section>
    </div>
  );
}

/**
 * A page only an Admin uses (billing, plan, API keys), opened by a Manager:
 * what it is for, and whom to ask. An Admin gets the page itself.
 */
export function AdminOnly({children}: {children: ReactNode}) {
  const {data} = useBrand();
  return data?.me.role === 'manager' ? <AdminsOnlyNotice /> : <>{children}</>;
}

function AdminsOnlyNotice() {
  const [admins, setAdmins] = useState<BrandTeamMember[] | null>(null);
  usePageTitle('Admins only');
  useEffect(() => {
    fetchTeam().then((team) => setAdmins(team.members.filter((member) => member.role === 'admin' && member.status === 'active')))
      .catch(() => setAdmins([]));
  }, []);
  return (
    <div className="page">
      <section className="card simple-state admin-only">
        <Icon name="lock" size={28} />
        <h1>An Admin handles this.</h1>
        <p>Billing, the plan, company details and API keys are kept to your team’s Admins. As a Manager you run the campaigns, creators, reviews and messages.</p>
        {admins?.length ? <p className="muted">Ask {admins.map((admin) => admin.name || admin.email).join(', ')}.</p> : null}
      </section>
    </div>
  );
}
